// Pure helpers for the Pro task UI (port of the mobile app's todoProModel.js and the
// TaskCardRow bucket / check model). No React and no I/O: ToDo.js and the task panels own
// the data; these only reshape it the way the Pro design shows it (Overdue / Today /
// Upcoming / Done sections, the week strip, "Since yesterday, 16:00" due lines …).
//
// Dates are grouped and labelled in Berlin time, like the rest of the ToDo page. Locale
// files are NOT imported here (importing one resets moment's global locale); every
// formatter sets the locale on its own instance and falls back to English.
import moment from "moment";
import "moment-timezone";

export const TODO_TZ = "Europe/Berlin";

/** Section order of the Pro list. */
export const PRO_SECTIONS = ["overdue", "today", "upcoming", "done"];

/** Time of day a new task is due at (design: "Today, 17:00"). */
export const PRO_DUE_HOUR = 17;

const WALL_FORMAT = "YYYY-MM-DD HH:mm";

const textOf = (value) => (typeof value === "string" ? value : typeof value === "number" ? String(value) : "");

const pad2 = (n) => String(n).padStart(2, "0");

/** "{{n}} of {{total}}" / "{count}" templates (the app's strings use both). */
export function fill(template, vars = {}) {
  return String(template == null ? "" : template).replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, (match, a, b) => {
    const key = a || b;
    return vars[key] != null ? String(vars[key]) : match;
  });
}

/** moment locale for the app language; Swiss German dialects read as German. */
export function localeOf(language) {
  const code = String(language || "en").split("-")[0].toLowerCase();
  const wanted = code === "de" || code === "gsw" || code === "wae" ? "de" : code === "en" ? "en-gb" : code;
  const loaded = moment.locales();
  if (loaded.indexOf(wanted) !== -1) return wanted;
  if (loaded.indexOf("en-gb") !== -1) return "en-gb";
  return "en";
}

const isGermanLocale = (loc) => loc === "de";

const berlin = (date) => {
  if (!date) return null;
  const m = moment.utc(date).tz(TODO_TZ);
  return m.isValid() ? m : null;
};

const todayOf = (now) => moment(now || new Date()).tz(TODO_TZ).startOf("day");

/** "2026-10-09" in Berlin time, or null. */
export function dayKeyOf(date) {
  const m = berlin(date);
  return m ? m.format("YYYY-MM-DD") : null;
}

/** Whole days from today (Berlin) to the date: -1 yesterday, 0 today, 1 tomorrow; null when invalid. */
export function dayDiffOf(date, now) {
  const m = berlin(date);
  if (!m) return null;
  return m.clone().startOf("day").diff(todayOf(now), "days");
}

/** Today's day key ("YYYY-MM-DD", Berlin). */
export function todayKeyOf(now) {
  return todayOf(now).format("YYYY-MM-DD");
}

/** The day key `days` days after `key` (negative = before). */
export function shiftDayKey(key, days) {
  const m = moment.tz(key, "YYYY-MM-DD", TODO_TZ);
  return m.isValid() ? m.add(days, "days").format("YYYY-MM-DD") : key;
}

/** Whether two day keys are in the same ISO week. */
export function sameWeek(a, b) {
  const ma = moment.tz(a, "YYYY-MM-DD", TODO_TZ);
  const mb = moment.tz(b, "YYYY-MM-DD", TODO_TZ);
  return ma.isValid() && mb.isValid() && ma.isSame(mb, "isoWeek");
}

/* ---- People ------------------------------------------------------------------ */

/** The assignees of a todo (assignedTo, then any extra ones from assignedUsers), with ids. */
export function usersOf(todo) {
  if (!todo) return [];
  const a = Array.isArray(todo.assignedTo) ? todo.assignedTo : [];
  const b = Array.isArray(todo.assignedUsers) ? todo.assignedUsers : [];
  const seen = new Set();
  const out = [];
  [...a, ...b].forEach((user) => {
    if (!user || typeof user !== "object" || user.id == null || user.id === "") return;
    const key = String(user.id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(user);
  });
  return out;
}

/** "Ben Wolf" from firstName / first_name / name / email. */
export function personName(user) {
  if (!user) return "";
  const first = textOf(user.firstName || user.first_name).trim();
  const last = textOf(user.lastName || user.last_name).trim();
  return `${first} ${last}`.trim() || textOf(user.fullName).trim() || textOf(user.name).trim() || textOf(user.email).trim();
}

/** Two letters for an avatar without a photo. */
export function initialsOf(name) {
  const words = textOf(name).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** Photo path of a user as the APIs send it (img / avatar / profilePicture). */
export const avatarPathOf = (user) => (user ? user.img || user.avatar || user.profilePicture || null : null);

/** Absolute photo URL (relative paths get the image base), or null. */
export function mediaUrl(path, base = "") {
  const raw = textOf(path).trim();
  if (!raw) return null;
  return /^https?:/i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:") ? raw : `${base || ""}${raw}`;
}

/** "1/2": assignees whose part is completed, of all assignees. null with fewer than two. */
export function assigneeProgress(todo) {
  const users = usersOf(todo);
  if (users.length < 2) return null;
  const done = users.filter((u) => u.userTodoStatus === "completed").length;
  return { done, total: users.length, value: done / users.length };
}

/* ---- Buckets ------------------------------------------------------------------ */

/**
 * overdue | today | upcoming | requested | bookmark | done | open (the mobile TaskCardRow
 * model). "requested" = I created it, my part is done (or I am not assigned) and others are
 * still open; "done" also covers a task whose own part I have finished.
 */
export function taskBucket(todo, { currentUserId, bookmarked, now } = {}) {
  if (!todo) return "open";
  if (todo.status === "confirmed") return "done";
  if (bookmarked) return "bookmark";
  const uid = currentUserId != null ? String(currentUserId) : "";
  const users = usersOf(todo);
  const mine = uid ? users.find((u) => String(u.id) === uid) : null;
  const mineOpen = !!mine && mine.userTodoStatus !== "completed";
  const createdByMe = !!uid && String(todo.createdById) === uid;
  const othersOpen = users.some((u) => String(u.id) !== uid && u.userTodoStatus !== "completed");
  if (createdByMe && !mineOpen && othersOpen) return "requested";
  const allDone = users.length > 0 && users.every((u) => u.userTodoStatus === "completed");
  if (todo.status === "completed" || allDone || (mine && !mineOpen)) return "done";
  const diff = dayDiffOf(todo.createdAt, now);
  if (diff == null) return "open";
  if (diff < 0) return "overdue";
  if (diff === 0) return "today";
  return "upcoming";
}

/**
 * What the row's check circle does: 'open' (mark my part done), 'done' (my part is done – a
 * click reopens it), 'confirm' (the creator verifies once every assignee is done),
 * 'confirmed' (read-only), null (nothing for me to complete).
 */
export function taskCheckState(todo, currentUserId) {
  if (!todo) return null;
  if (todo.status === "confirmed") return "confirmed";
  const uid = currentUserId != null ? String(currentUserId) : "";
  const users = usersOf(todo);
  const createdByMe = !!uid && String(todo.createdById) === uid;
  const allDone = users.length > 0 && users.every((u) => u.userTodoStatus === "completed");
  if (createdByMe && allDone) return "confirm";
  const mine = uid ? users.find((u) => String(u.id) === uid) : null;
  if (!mine) return null;
  return mine.userTodoStatus === "completed" ? "done" : "open";
}

/**
 * Pro section of a row: the bucket where it is one of the design's, otherwise by its date
 * ("requested" and dateless "open" tasks sit with their day).
 */
export function proBucket(bucket, todo, now) {
  if (bucket === "overdue" || bucket === "today" || bucket === "upcoming" || bucket === "done" || bucket === "bookmark") return bucket;
  const d = dayDiffOf(todo && todo.createdAt, now);
  if (d == null) return "upcoming";
  if (d < 0) return "overdue";
  return d === 0 ? "today" : "upcoming";
}

const timeOf = (todo) => {
  const m = berlin(todo && todo.createdAt);
  return m ? m.valueOf() : 0;
};

/**
 * Group rows into the Pro sections. `entries` = [{ todo, section }] (section = proBucket).
 * dayKey (optional) keeps only that Berlin day. Overdue and Done list the latest first;
 * Today and Upcoming the soonest first.
 */
export function groupForPro(entries, { dayKey } = {}) {
  const out = { overdue: [], today: [], upcoming: [], done: [] };
  (entries || []).forEach((e) => {
    if (!e || !e.todo || !out[e.section]) return;
    if (dayKey && dayKeyOf(e.todo.createdAt) !== dayKey) return;
    out[e.section].push(e);
  });
  out.overdue.sort((a, b) => timeOf(b.todo) - timeOf(a.todo));
  out.today.sort((a, b) => timeOf(a.todo) - timeOf(b.todo));
  out.upcoming.sort((a, b) => timeOf(a.todo) - timeOf(b.todo));
  out.done.sort((a, b) => timeOf(b.todo) - timeOf(a.todo));
  return out;
}

/* ---- Week strip ------------------------------------------------------------------ */

const weekdayOf = (m, loc) => m.clone().locale(loc).format(isGermanLocale(loc) ? "dd" : "ddd");

/**
 * The week strip (Mon–Sun of the anchor's Berlin week; default this week). Each day: key, wd,
 * n, today, and from `entries` (open = not done) `has` (a task is due) and `late` (an overdue one).
 */
export function weekOf(entries, { now, language, anchor } = {}) {
  const loc = localeOf(language);
  const today = todayOf(now);
  const base = anchor ? moment.tz(anchor, "YYYY-MM-DD", TODO_TZ) : null;
  const start = (base && base.isValid() ? base : today).clone().startOf("isoWeek");
  const open = {};
  const late = {};
  (entries || []).forEach((e) => {
    if (!e || !e.todo || e.section === "done" || e.section === "bookmark") return;
    const key = dayKeyOf(e.todo.createdAt);
    if (!key) return;
    open[key] = true;
    if (e.section === "overdue") late[key] = true;
  });
  return Array.from({ length: 7 }, (_, i) => {
    const d = start.clone().add(i, "days");
    const key = d.format("YYYY-MM-DD");
    return { key, wd: weekdayOf(d, loc), n: d.date(), today: d.isSame(today, "day"), has: !!open[key], late: !!late[key] };
  });
}

/** "October 2026" / "Oktober 2026" for the week strip header (the week's Thursday month). */
export function monthLabelOf(key, language) {
  const loc = localeOf(language);
  const m = moment.tz(key, "YYYY-MM-DD", TODO_TZ);
  if (!m.isValid()) return "";
  return m.clone().startOf("isoWeek").add(3, "days").locale(loc).format("MMMM YYYY");
}

/** "Fri 10 Oct" / "Fr 10. Okt." for the "Showing …" chip. */
export function dayLabelOf(key, language) {
  const loc = localeOf(language);
  const m = moment.tz(key, "YYYY-MM-DD", TODO_TZ);
  if (!m.isValid()) return "";
  return m.locale(loc).format(isGermanLocale(loc) ? "dd D. MMM" : "ddd D MMM");
}

/* ---- Labels ------------------------------------------------------------------------ */

/**
 * Due line of a task: "Today, 10:30"; "Since yesterday, 16:00" (overdue) or "Yesterday, 16:00";
 * "Tue, 12:00" in the past week; "Fri, 10 Oct" / "Fr, 10. Okt." otherwise.
 * words = { today, yesterday, sinceYesterday } (localised).
 */
export function dueLabel(date, { language, now, overdue, words = {} } = {}) {
  const m = berlin(date);
  if (!m) return "";
  const loc = localeOf(language);
  const de = isGermanLocale(loc);
  const local = m.clone().locale(loc);
  const time = m.format("HH:mm");
  const diff = m.clone().startOf("day").diff(todayOf(now), "days");
  if (diff === 0) return `${words.today || "Today"}, ${time}`;
  if (diff === -1) return `${overdue ? words.sinceYesterday || "Since yesterday" : words.yesterday || "Yesterday"}, ${time}`;
  if (diff < 0 && diff > -7) return `${local.format(de ? "dd" : "ddd")}, ${time}`;
  return local.format(de ? "dd, D. MMM" : "ddd, D MMM");
}

/** "Fri, 10.10.2026 · 14:00" style full date for edit rows. */
export function fullDateLabel(date, language) {
  const m = berlin(date);
  if (!m) return "";
  const loc = localeOf(language);
  return m.clone().locale(loc).format(isGermanLocale(loc) ? "dd, DD.MM.YYYY · HH:mm" : "ddd, DD.MM.YYYY · HH:mm");
}

/** Activity time: "08:41" today, "Mon" this week, "02.10." before. */
export function shortWhen(date, { language, now } = {}) {
  const m = berlin(date);
  if (!m) return "";
  const diff = m.clone().startOf("day").diff(todayOf(now), "days");
  if (diff === 0) return m.format("HH:mm");
  if (Math.abs(diff) < 7) return m.clone().locale(localeOf(language)).format("ddd");
  return m.format("DD.MM.");
}

/** Material Symbols glyph for a history entry, from its raw log text and status. */
export function activityIcon(raw, status) {
  const d = textOf(raw).trim().toLowerCase();
  if (d.includes("delet")) return "delete";
  if (d.includes("unassign") || d.includes("removed")) return "person_remove";
  if (d.includes("assign")) return "person_add";
  if (d.includes("confirm") || status === "confirmed") return "task_alt";
  if (d.includes("complet") || status === "completed") return "check_circle";
  if (d.includes("progress") || status === "in-progress") return "pending";
  if (d.includes("creat")) return "add_task";
  if (d.includes("date") || d.includes("updat") || d.includes("edit")) return "edit_calendar";
  return "history";
}

/** Plain task text: markdown markers removed and "@[Name](id)" mentions shown as "@Name". */
export function plainTodoText(value) {
  const text = textOf(value);
  if (!text) return "";
  return text
    .replace(/@\[([^\]]+)\]\([^)]*\)/g, "@$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/~~(.*?)~~/g, "$1")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, "")
    .replace(/^[ \t]*>[ \t]?/gm, "");
}

/**
 * Title and description of the detail page from the one text a task has: the first line is the
 * title (shortened past 120 characters), the rest – or the whole text when the title had to be
 * shortened – is the description.
 */
export function splitTitle(text) {
  const full = textOf(text).trim();
  if (!full) return { title: "", note: "" };
  const br = full.indexOf("\n");
  const head = (br >= 0 ? full.slice(0, br) : full).trim();
  if (head.length > 120) {
    const cut = head.slice(0, 110);
    const space = cut.lastIndexOf(" ");
    return { title: `${(space > 60 ? cut.slice(0, space) : cut).trim()}…`, note: full };
  }
  return { title: head, note: br >= 0 ? full.slice(br + 1).trim() : "" };
}

/* ---- Rooms, chats, sources ----------------------------------------------------------- */

const ROOM_KEYS = ["roomName", "buildingName", "projectName"];

/** The room a todo is filed in, or "". */
export function roomNameOf(todo) {
  if (!todo) return "";
  for (let i = 0; i < ROOM_KEYS.length; i += 1) {
    const v = textOf(todo[ROOM_KEYS[i]]).trim();
    if (v) return v;
  }
  return textOf(todo.room && todo.room.name).trim() || textOf(todo.building && todo.building.name).trim();
}

/** The chat a todo came from, or "". */
export function groupNameOf(todo) {
  if (!todo) return "";
  return textOf(todo.groupName).trim() || textOf(todo.chatGroupName).trim() || textOf(todo.chatGroup && todo.chatGroup.name).trim();
}

/** A room name short enough for the Filter button: whole words up to `max` characters, then "…". */
export function shortRoomName(name, max = 18) {
  const full = textOf(name).trim().replace(/\s+/g, " ");
  if (full.length <= max) return full;
  const cut = full.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  const head = (space >= Math.floor(max / 2) ? cut.slice(0, space) : cut).replace(/[\s,.;:–-]+$/, "");
  return `${head}…`;
}

/** Two-letter room code for room tiles ("Neubau Kita Sonnenhof" → "NK", "Kita" → "KI"). */
export function roomCodeOf(name) {
  return initialsOf(name);
}

/** "1 task" / "12 tasks" (templates: one, many with {{n}}). */
export function taskCountLabel(n, one, many) {
  const count = Number(n) || 0;
  return count === 1 ? one : fill(many, { n: count });
}

const idOf = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value.id || value._id || null;
  return value;
};

/** The room id a todo is filed in (roomId / buildingList, id or object), or null. */
export function roomIdOf(todo) {
  if (!todo) return null;
  const id = idOf(todo.roomId) || idOf(todo.buildingList) || idOf(todo.room) || idOf(todo.building);
  return id != null ? String(id) : null;
}

/**
 * Whether the current user is a member of the todo's room, from the rooms GET /building/my
 * lists ({ id, isJoin }). true / false, or null when it cannot be told.
 */
export function roomMembershipOf(todo, rooms) {
  const id = roomIdOf(todo);
  if (!id || !Array.isArray(rooms) || !rooms.length) return null;
  const room = rooms.find((r) => r && String(r.id != null ? r.id : r._id) === id);
  if (!room) return null;
  if (typeof room.isJoin === "boolean") return room.isJoin;
  if (room.isJoin === 1 || room.isJoin === "1") return true;
  if (room.isJoin === 0 || room.isJoin === "0") return false;
  return null;
}

/**
 * Where a todo came from: { kind: 'email', emailMessageId, emailAccountId } for one saved from an
 * e-mail, { kind: 'chat', groupId, messageId } for one saved from a chat, else null.
 */
export function todoSourceOf(todo) {
  if (!todo) return null;
  const emailMessageId = idOf(todo.emailMessageId) || idOf(todo.emailId);
  if (emailMessageId) {
    const emailAccountId = idOf(todo.emailAccountId);
    return { kind: "email", emailMessageId: String(emailMessageId), emailAccountId: emailAccountId != null ? String(emailAccountId) : null };
  }
  const groupId = idOf(todo.groupId) || idOf(todo.chatId) || idOf(todo.chat);
  if (groupId) {
    const messageId = idOf(todo.messageId);
    return { kind: "chat", groupId: String(groupId), messageId: messageId != null ? String(messageId) : null };
  }
  return null;
}

/* ---- New task due options --------------------------------------------------------------- */

/**
 * Berlin wall-clock value ("YYYY-MM-DD HH:mm") of a "New task" Due option: Today / Tomorrow at
 * 17:00. Late on the day itself the time moves on (two hours from now, 23:59 at the latest) so a
 * new task never starts out overdue.
 */
export function proDueValue(option = "today", now) {
  const at = moment(now || new Date()).tz(TODO_TZ);
  const day = option === "tomorrow" ? at.clone().add(1, "day") : at.clone();
  let hour = PRO_DUE_HOUR;
  let minute = 0;
  if (day.isSame(at, "day") && at.hours() >= PRO_DUE_HOUR - 1) {
    const next = at.hours() + 2;
    if (next <= 23) hour = next;
    else {
      hour = 23;
      minute = 59;
    }
  }
  return `${day.format("YYYY-MM-DD")} ${pad2(hour)}:${pad2(minute)}`;
}

/** The due value of a day picked in the calendar: 17:00 that day, or today's bumped time. */
export function proPickedDueValue(dayKey, now) {
  if (!dayKey) return proDueValue("today", now);
  if (dayKey === todayKeyOf(now)) return proDueValue("today", now);
  return `${dayKey} ${pad2(PRO_DUE_HOUR)}:00`;
}

/** Which option a due value matches: 'today' | 'tomorrow' | null (a picked day). */
export function dueOptionOfValue(value, now) {
  const key = textOf(value).slice(0, 10);
  if (!key) return null;
  if (key === todayKeyOf(now)) return "today";
  if (key === shiftDayKey(todayKeyOf(now), 1)) return "tomorrow";
  return null;
}

/** "Thu, 16 Oct" / "Do, 16. Okt." for the "Pick date" chip. */
export function dueChipLabel(value, language) {
  const key = textOf(value).slice(0, 10);
  const m = moment.tz(key, "YYYY-MM-DD", TODO_TZ);
  if (!m.isValid()) return "";
  const loc = localeOf(language);
  return m.locale(loc).format(isGermanLocale(loc) ? "dd, D. MMM" : "ddd, D MMM");
}

/** A Berlin wall-clock value as the API wants it: UTC "YYYY-MM-DD HH:mm:ss". */
export function wallToUtcString(value) {
  const m = moment.tz(textOf(value), WALL_FORMAT, TODO_TZ);
  return m.isValid() ? m.utc().format("YYYY-MM-DD HH:mm:ss") : null;
}

/** A UTC instant as a Berlin wall-clock value ("YYYY-MM-DD HH:mm"). */
export function utcToWall(date) {
  const m = berlin(date);
  return m ? m.format(WALL_FORMAT) : "";
}

/* ---- Filters ----------------------------------------------------------------------------- */

/** How many filter dimensions are on ("Filter · N"). */
export function filterCount({ search, status, assignment, projects, users } = {}) {
  let n = 0;
  if (textOf(search).trim()) n += 1;
  if (status) n += 1;
  if (assignment) n += 1;
  n += Array.isArray(projects) ? projects.length : 0;
  n += Array.isArray(users) ? users.length : 0;
  return n;
}
